import {
  AlertTriangle,
  ArrowDownAZ,
  BarChart3,
  BookOpen,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  FileText,
  GraduationCap,
  Layout,
  ListChecks,
  ListFilter,
  Loader2,
  Pencil,
  Plus,
  Search,
  Users,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  EbookCard,
  EbookListSkeleton,
  EmptyState,
  Input,
  Modal,
  PageLayout,
  Pagination,
  Select,
} from "../components";
import type { EbookEntry } from "../components/cards/EbookCard";
import EbookEditor from "../components/features/EbookEditor";
import { HEADER_PRIMARY_BUTTON } from "../components/layout/headerActionStyles";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import { cn } from "../utils/cn";

interface Ebook {
  ebook_id: string;
  title: string;
  subtitle?: string;
  description: string;
  template: string;
  status: string;
  target_audience: string;
  estimated_read_time: number;
  word_count: number;
  download_count: number;
  lead_count: number;
  author?: string;
  company?: string;
  created_at: string;
  published_at?: string;
  chapters?: any[];
  keywords?: string[];
}

interface Lead {
  lead_id: string;
  ebook_id: string;
  name: string;
  email: string;
  company: string;
  job_title: string;
  industry: string;
  status: string;
  score: {
    total_score: number;
  };
  created_at: string;
}

interface EbookTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  estimatedTime: string;
  chapters: number;
}

const EbookList: React.FC = () => {
  const { t } = useTranslation();
  const [ebooks, setEbooks] = useState<Ebook[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { activeContext } = useActiveContext();

  // Estados para modais
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // Vira `true` na primeira tentativa de envio com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [createAttemptedSubmit, setCreateAttemptedSubmit] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ebookToDelete, setEbookToDelete] = useState<EbookEntry | null>(null);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [selectedEbookForLeads, setSelectedEbookForLeads] =
    useState<EbookEntry | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedEbook, setSelectedEbook] = useState<Ebook | null>(null);

  // Estados para busca e filtros
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterTemplate, setFilterTemplate] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"date" | "name" | "downloads" | "leads">(
    "date",
  );

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  // Estados para criação de ebook
  const [ebookForm, setEbookForm] = useState({
    topic: "",
    template: "business_guide",
    targetAudience: "profissionais",
    companyName: "Virtus Automation",
    author: "Equipe Virtus Automation",
  });

  const templates: EbookTemplate[] = [
    {
      id: "business_guide",
      name: t("Guia de Negócios"),
      description: t(
        "Guia completo para implementação de soluções empresariais",
      ),
      icon: "📊",
      estimatedTime: "15-20 min",
      chapters: 5,
    },
    {
      id: "technical_tutorial",
      name: t("Tutorial Técnico"),
      description: t("Passo a passo para implementação técnica"),
      icon: "⚙️",
      estimatedTime: "20-25 min",
      chapters: 6,
    },
    {
      id: "case_study",
      name: t("Estudo de Caso"),
      description: t("Análise detalhada de casos de sucesso"),
      icon: "📈",
      estimatedTime: "10-15 min",
      chapters: 4,
    },
    {
      id: "whitepaper",
      name: t("Whitepaper"),
      description: t("Documento técnico e acadêmico"),
      icon: "📋",
      estimatedTime: "25-30 min",
      chapters: 7,
    },
    {
      id: "checklist",
      name: t("Checklist"),
      description: t("Lista prática de verificação"),
      icon: "✅",
      estimatedTime: "5-10 min",
      chapters: 3,
    },
  ];

  useEffect(() => {
    setEbooks([]);
    setLeads([]);
    setLoading(true);
    loadEbooks();
    loadLeads();
  }, [
    activeContext.type,
    activeContext.organization?.id,
    activeContext.clientCompany?.id,
  ]);

  const loadEbooks = async () => {
    setLoading(true);
    try {
      const result = await apiService.get(
        `${API_ENDPOINTS.EBOOKS.LIST}?t=${Date.now()}`,
      );
      if (result.success) {
        // Forçar re-render criando um novo array
        const newEbooks = [...result.ebooks];
        setEbooks(newEbooks);
      }
    } catch (err: any) {
      setError(t("Erro ao carregar ebooks"));
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const result = await apiService.get(API_ENDPOINTS.LEADS.LIST);
      if (result.success) {
        setLeads(result.leads);
      }
    } catch (err: any) {
      console.error("Erro ao carregar leads:", err);
    }
  };

  const createEbook = async () => {
    setCreateAttemptedSubmit(true);
    if (!ebookForm.topic.trim()) {
      setError(t("Tópico é obrigatório"));
      return;
    }

    setLoading(true);
    try {
      const result = await apiService.post(API_ENDPOINTS.EBOOKS.CREATE, {
        topic: ebookForm.topic,
        template: ebookForm.template,
        target_audience: ebookForm.targetAudience,
        company_info: {
          name: ebookForm.companyName,
          author: ebookForm.author,
        },
      });
      if (result.success) {
        setCreateModalOpen(false);
        setEbookForm({
          topic: "",
          template: "business_guide",
          targetAudience: "profissionais",
          companyName: "Virtus Automation",
          author: "Equipe Virtus Automation",
        });
        showToastSuccess(t("Ebook criado com sucesso!"));
        await loadEbooks();
      } else {
        setError(result.error || t("Erro ao criar ebook"));
        showToastError(result.error || t("Erro ao criar ebook"));
      }
    } catch (err: any) {
      setError(t("Erro ao criar ebook"));
      showToastError(t("Erro ao criar ebook"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ebook: EbookEntry) => {
    try {
      await apiService.delete(
        API_ENDPOINTS.EBOOKS.DELETE.replace("{id}", ebook.ebook_id),
      );

      showToastSuccess(t("Ebook excluído com sucesso!"));
      setDeleteModalOpen(false);
      setEbookToDelete(null);
      await loadEbooks();
    } catch (err: any) {
      showToastError(err.message || t("Erro ao excluir ebook."));
    }
  };

  const handleEditEbook = async (ebook: EbookEntry) => {
    try {
      // Buscar ebook completo com conteúdo
      const result = await apiService.get(
        API_ENDPOINTS.EBOOKS.GET.replace("{id}", ebook.ebook_id),
      );
      if (result.success && result.ebook) {
        setSelectedEbook(result.ebook);
        setShowEditor(true);
      } else {
        // Fallback: usar ebook da lista
        const fullEbook = ebooks.find((e) => e.ebook_id === ebook.ebook_id);
        if (fullEbook) {
          setSelectedEbook(fullEbook);
          setShowEditor(true);
        }
      }
    } catch (err) {
      console.error("Erro ao buscar ebook completo:", err);
      // Fallback: usar ebook da lista
      const fullEbook = ebooks.find((e) => e.ebook_id === ebook.ebook_id);
      if (fullEbook) {
        setSelectedEbook(fullEbook);
        setShowEditor(true);
      }
    }
  };

  const handleSaveEbook = async (updatedEbook: any) => {
    try {
      // Converter capítulos em conteúdo markdown se existir
      let content = updatedEbook.content || "";
      if (updatedEbook.chapters && updatedEbook.chapters.length > 0) {
        content = updatedEbook.chapters
          .map(
            (chapter: any) => `# ${chapter.title}\n\n${chapter.content || ""}`,
          )
          .join("\n\n---\n\n");
      }

      const requestBody = {
        title: updatedEbook.title,
        subtitle: updatedEbook.subtitle,
        description: updatedEbook.description,
        author: updatedEbook.author,
        company: updatedEbook.company,
        target_audience: updatedEbook.target_audience,
        template: updatedEbook.template,
        status: updatedEbook.status,
        content: content,
      };

      const result = await apiService.put(
        API_ENDPOINTS.EBOOKS.UPDATE.replace("{id}", updatedEbook.ebook_id),
        requestBody,
      );

      if (result.success) {
        await loadEbooks();
        setShowEditor(false);
        setSelectedEbook(null);
        showToastSuccess(t("Ebook salvo com sucesso!"));
        return result;
      } else {
        throw new Error(result.error || t("Erro ao salvar ebook"));
      }
    } catch (err) {
      console.error("Erro ao salvar ebook:", err);
      showToastError(
        err instanceof Error ? err.message : t("Erro ao salvar ebook"),
      );
      throw err;
    }
  };

  const downloadEbook = async (ebookId: string) => {
    try {
      await apiService.downloadFile(
        API_ENDPOINTS.EBOOKS.DOWNLOAD.replace("{id}", ebookId),
        `ebook-${ebookId}.pdf`,
      );
      showToastSuccess(t("Download iniciado!"));
    } catch (err: any) {
      showToastError(err.message || t("Erro ao baixar ebook"));
    }
  };

  // Filtrar e ordenar ebooks
  const filteredAndSortedEbooks = useMemo(() => {
    let filtered = [...ebooks];

    // Filtro por busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (ebook) =>
          ebook.title.toLowerCase().includes(query) ||
          ebook.description?.toLowerCase().includes(query) ||
          ebook.template?.toLowerCase().includes(query),
      );
    }

    // Filtro por status
    if (filterStatus !== "all") {
      filtered = filtered.filter((ebook) => ebook.status === filterStatus);
    }

    // Filtro por template
    if (filterTemplate !== "all") {
      filtered = filtered.filter((ebook) => ebook.template === filterTemplate);
    }

    // Ordenação
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.title.localeCompare(b.title);
        case "downloads":
          return b.download_count - a.download_count;
        case "leads":
          return b.lead_count - a.lead_count;
        case "date":
        default:
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
      }
    });

    return filtered;
  }, [ebooks, searchQuery, filterStatus, filterTemplate, sortBy]);

  // Paginação
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedEbooks.length / itemsPerPage),
  );
  const paginatedEbooks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedEbooks.slice(startIndex, endIndex);
  }, [filteredAndSortedEbooks, currentPage, itemsPerPage]);

  // Resetar para primeira página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterStatus, filterTemplate, sortBy]);

  // Estatísticas de filtros
  const filterStats = useMemo(() => {
    const total = ebooks.length;
    const filtered = filteredAndSortedEbooks.length;
    const byStatus = {
      published: ebooks.filter((e) => e.status === "published").length,
      draft: ebooks.filter((e) => e.status === "draft").length,
      generating: ebooks.filter((e) => e.status === "generating").length,
    };
    const byTemplate = {
      business_guide: ebooks.filter((e) => e.template === "business_guide")
        .length,
      technical_tutorial: ebooks.filter(
        (e) => e.template === "technical_tutorial",
      ).length,
      case_study: ebooks.filter((e) => e.template === "case_study").length,
      whitepaper: ebooks.filter((e) => e.template === "whitepaper").length,
      checklist: ebooks.filter((e) => e.template === "checklist").length,
    };
    return { total, filtered, byStatus, byTemplate };
  }, [ebooks, filteredAndSortedEbooks]);

  const getLeadStatusColor = (status: string) => {
    switch (status) {
      case "new":
        return "bg-blue-100 text-blue-800";
      case "contacted":
        return "bg-yellow-100 text-yellow-800";
      case "qualified":
        return "bg-green-100 text-green-800";
      case "converted":
        return "bg-purple-100 text-purple-800";
      case "lost":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <PageLayout
      title={t("Gerenciar Ebooks")}
      subtitle={t(
        "Crie, edite e gerencie seus ebooks para captura de leads e materiais ricos.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-4"
      loading={loading}
      skeleton={<EbookListSkeleton />}
      actions={
        <Button
          onClick={() => setCreateModalOpen(true)}
          variant="primary"
          size="sm"
          className={HEADER_PRIMARY_BUTTON}
          icon={<Plus className="h-3.5 w-3.5" />}
        >
          <span className="hidden sm:inline text-xs">{t("Novo Ebook")}</span>
          <span className="sm:hidden text-xs">{t("Novo")}</span>
        </Button>
      }
    >
      {/* Filter Bar - full-bleed sticky bar, mesmo padrão de "Postagens" */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 pl-4 pr-2 xl:pl-5 xl:pr-3 py-1.5 bg-white/95 backdrop-blur-md border-b border-purple-200 flex items-center justify-between">
        {/* Wrapper dos filtros */}
        <div className="w-full">
          {/* Linha única com flex-wrap para ajuste automático */}
          <div className="flex flex-row flex-wrap gap-2 sm:gap-2.5 items-center">
            {/* Busca */}
            <div className="flex-1 min-w-[200px] sm:min-w-[250px]">
              <Input
                type="text"
                placeholder={t("Buscar ebooks...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputSize="sm"
                icon={<Search className="h-4 w-4 text-gray-400" />}
                className="w-full h-8! sm:h-9!"
              />
            </div>

            {/* Filtros em Desktop (Pills Discretos) */}
            <div className="hidden xl:flex items-center gap-2">
              {filterStatus !== "all" && (
                <Badge
                  variant="outline"
                  className="bg-purple-50 text-purple-700 border-purple-200 font-bold uppercase text-[9px]"
                >
                  {t("Status:")} {filterStatus}
                </Badge>
              )}
              {filterTemplate !== "all" && (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-700 border-blue-200 font-bold uppercase text-[9px]"
                >
                  {t("Template:")} {filterTemplate}
                </Badge>
              )}
            </div>

            {/* Dropdowns Compactos */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-[175px] sm:w-[210px] shrink-0">
                <Select
                  value={filterStatus}
                  onChange={(value) => setFilterStatus(value)}
                  showSelectedCount
                  options={[
                    {
                      value: "all",
                      label: t("Todos Status"),
                      icon: <ListFilter />,
                      count: filterStats.total,
                    },
                    {
                      value: "published",
                      label: t("Publicados"),
                      icon: <CheckCircle />,
                      count: filterStats.byStatus.published,
                    },
                    {
                      value: "draft",
                      label: t("Rascunho"),
                      icon: <Pencil />,
                      count: filterStats.byStatus.draft,
                    },
                    {
                      value: "generating",
                      label: t("Gerando"),
                      icon: <Loader2 />,
                      count: filterStats.byStatus.generating,
                    },
                  ]}
                  size="sm"
                />
              </div>

              <div className="w-[180px] sm:w-[220px] shrink-0">
                <Select
                  value={filterTemplate}
                  onChange={(value) => setFilterTemplate(value)}
                  showSelectedCount
                  options={[
                    {
                      value: "all",
                      label: t("Templates"),
                      icon: <Layout />,
                      count: filterStats.total,
                    },
                    {
                      value: "business_guide",
                      label: t("Guia Negócios"),
                      icon: <BookOpen />,
                      count: filterStats.byTemplate.business_guide,
                    },
                    {
                      value: "technical_tutorial",
                      label: t("Tutorial"),
                      icon: <GraduationCap />,
                      count: filterStats.byTemplate.technical_tutorial,
                    },
                    {
                      value: "case_study",
                      label: t("Estudo Caso"),
                      icon: <BarChart3 />,
                      count: filterStats.byTemplate.case_study,
                    },
                    {
                      value: "whitepaper",
                      label: t("Whitepaper"),
                      icon: <FileText />,
                      count: filterStats.byTemplate.whitepaper,
                    },
                    {
                      value: "checklist",
                      label: t("Checklist"),
                      icon: <ListChecks />,
                      count: filterStats.byTemplate.checklist,
                    },
                  ]}
                  size="sm"
                />
              </div>

              <div className="w-[160px] sm:w-[195px] shrink-0">
                <Select
                  value={sortBy}
                  onChange={(value) =>
                    setSortBy(value as "date" | "name" | "downloads" | "leads")
                  }
                  options={[
                    { value: "date", label: t("Ordenação"), icon: <Clock /> },
                    {
                      value: "name",
                      label: t("Nome (A-Z)"),
                      icon: <ArrowDownAZ />,
                    },
                    {
                      value: "downloads",
                      label: t("Downloads"),
                      icon: <Download />,
                    },
                    { value: "leads", label: t("Leads"), icon: <Users /> },
                  ]}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Indicador discreto de resultados */}
          {ebooks.length > 0 &&
            (filterStatus !== "all" ||
              filterTemplate !== "all" ||
              searchQuery) && (
              <div className="mt-3 pt-3 border-t border-purple-200 flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-400">
                <div className="flex items-center gap-2">
                  <span className="bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                    {filterStats.filtered}
                  </span>
                  <span>{t("resultados encontrados")}</span>
                </div>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setFilterStatus("all");
                    setFilterTemplate("all");
                  }}
                  className="text-purple-600 hover:text-purple-700 transition-colors uppercase tracking-widest"
                >
                  {t("Limpar Filtros")}
                </button>
              </div>
            )}
        </div>
      </div>

      <div className="w-full flex-1 mt-2!">
        {ebooks.length === 0 ? (
          <div className="py-12 sm:py-16">
            <EmptyState
              icon={<BookOpen className="h-16 w-16 sm:h-20 sm:w-20" />}
              title={t("Nenhum ebook criado")}
              description={t(
                "Crie seu primeiro ebook para começar a capturar leads através de downloads.",
              )}
              action={
                <Button
                  onClick={() => setCreateModalOpen(true)}
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  size="md"
                >
                  {t("Criar Primeiro Ebook")}
                </Button>
              }
            />
          </div>
        ) : filteredAndSortedEbooks.length === 0 ? (
          <div className="py-12 sm:py-16">
            <EmptyState
              icon={
                <Search className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
              }
              title={t("Nenhum ebook encontrado")}
              description={`${t("Não foram encontrados ebooks com os filtros aplicados.")}${searchQuery ? ` ${t("Busca:")} "${searchQuery}"` : ""}`}
              action={
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    onClick={() => {
                      setSearchQuery("");
                      setFilterStatus("all");
                      setFilterTemplate("all");
                      setSortBy("date");
                    }}
                    variant="secondary"
                    size="md"
                  >
                    {t("Limpar Filtros")}
                  </Button>
                  <Button
                    onClick={() => setCreateModalOpen(true)}
                    variant="primary"
                    icon={<Plus className="h-4 w-4" />}
                    size="md"
                  >
                    {t("Criar Novo Ebook")}
                  </Button>
                </div>
              }
            />
          </div>
        ) : (
          <>
            {/* Grid de Ebooks - Sincronizado com o padrão de 4 colunas em telas XL */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {paginatedEbooks.map((ebook) => (
                <EbookCard
                  key={ebook.ebook_id}
                  ebook={ebook}
                  onView={async (ebook) => {
                    try {
                      // Buscar ebook completo com conteúdo
                      const result = await apiService.get(
                        API_ENDPOINTS.EBOOKS.GET.replace(
                          "{id}",
                          ebook.ebook_id,
                        ),
                      );
                      if (result.success && result.ebook) {
                        setSelectedEbook(result.ebook);
                        setShowEditor(true);
                      } else {
                        // Fallback: usar ebook da lista
                        const fullEbook = ebooks.find(
                          (e) => e.ebook_id === ebook.ebook_id,
                        );
                        if (fullEbook) {
                          setSelectedEbook(fullEbook);
                          setShowEditor(true);
                        }
                      }
                    } catch (err) {
                      console.error("Erro ao buscar ebook completo:", err);
                      // Fallback: usar ebook da lista
                      const fullEbook = ebooks.find(
                        (e) => e.ebook_id === ebook.ebook_id,
                      );
                      if (fullEbook) {
                        setSelectedEbook(fullEbook);
                        setShowEditor(true);
                      }
                    }
                  }}
                  onEdit={handleEditEbook}
                  onDelete={(ebook) => {
                    setEbookToDelete(ebook);
                    setDeleteModalOpen(true);
                  }}
                  onDownload={downloadEbook}
                  onViewLeads={(ebook) => {
                    setSelectedEbookForLeads(ebook);
                    setShowLeadsModal(true);
                  }}
                />
              ))}
            </div>

            {/* Footer com paginação estilo pill */}
            <div className="mt-8 flex justify-center w-full">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredAndSortedEbooks.length}
                itemsPerPage={itemsPerPage}
                onPageChange={(page: number) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                showInfo={true}
                maxVisiblePages={5}
              />
            </div>
          </>
        )}
      </div>

      {/* Modal de Criação */}
      {createModalOpen && (
        <Modal
          open={createModalOpen}
          onClose={() => {
            setCreateModalOpen(false);
            setCreateAttemptedSubmit(false);
          }}
          title={t("Criar Novo Ebook")}
          subtitle={t(
            "Defina o tema e o template para iniciar a geração com IA",
          )}
          icon={<Plus className="h-5 w-5" />}
          headerVariant="gradient"
          maxWidth="max-w-4xl"
          footer={
            <div className="flex flex-row justify-end gap-3 w-full">
              <Button
                type="button"
                onClick={() => {
                  setCreateModalOpen(false);
                  setCreateAttemptedSubmit(false);
                }}
                size="sm"
                variant="secondary"
                disabled={loading}
              >
                {t("Cancelar")}
              </Button>
              <Button
                type="submit"
                form="create-ebook-form"
                size="sm"
                variant="primary"
                disabled={loading || !ebookForm.topic.trim()}
                isLoading={loading}
                className="shadow-lg shadow-purple-500/20 px-6"
              >
                {loading ? t("Criando...") : t("Iniciar Geração")}
              </Button>
            </div>
          }
        >
          <form
            id="create-ebook-form"
            onSubmit={(e) => {
              e.preventDefault();
              createEbook();
            }}
            className="space-y-6"
          >
            {error && (
              <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-shake">
                <div className="shrink-0 mt-0.5">
                  <svg
                    className="h-4 w-4 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="font-medium">{error}</span>
              </div>
            )}

            {/* Tópico */}
            <div className="space-y-2">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                {t("Tema Central do Ebook")}{" "}
                <span className="text-red-400">*</span>
              </label>
              <Input
                type="text"
                value={ebookForm.topic}
                onChange={(e) =>
                  setEbookForm({ ...ebookForm, topic: e.target.value })
                }
                placeholder={t(
                  "Ex: Como escalar seu Marketing B2B com Automação",
                )}
                required
                error={
                  createAttemptedSubmit && !ebookForm.topic.trim()
                    ? t("O tema do ebook é obrigatório")
                    : undefined
                }
                inputSize="sm"
                className="bg-gray-50! focus:bg-white! transition-all shadow-inner"
              />
            </div>

            {/* Template */}
            <div className="space-y-3">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                {t("Estrutura de Conteúdo (Template)")}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {templates.map((template) => {
                  const isActive = ebookForm.template === template.id;
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() =>
                        setEbookForm({ ...ebookForm, template: template.id })
                      }
                      className={cn(
                        "relative flex flex-col items-start gap-3 p-4 rounded-2xl border-2 transition-all duration-300 text-left group",
                        "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1",
                        isActive
                          ? "border-transparent bg-purple-600 text-white shadow-xl scale-[1.02]"
                          : "border-transparent bg-gray-50/80 text-gray-700 hover:bg-white hover:shadow-md",
                      )}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div
                          className={cn(
                            "p-2 rounded-xl transition-colors shadow-xs",
                            isActive ? "bg-white/20" : "bg-white",
                          )}
                        >
                          <span className="text-xl">{template.icon}</span>
                        </div>
                        {isActive && (
                          <CheckCircle className="h-4 w-4 text-white/90" />
                        )}
                      </div>

                      <div className="space-y-1">
                        <span className="block text-xs font-bold uppercase tracking-tight">
                          {template.name}
                        </span>
                        <p
                          className={cn(
                            "text-[10px] leading-relaxed line-clamp-2",
                            isActive ? "text-white/80" : "text-gray-500",
                          )}
                        >
                          {template.description}
                        </p>
                      </div>

                      <div
                        className={cn(
                          "flex items-center gap-3 pt-3 mt-auto w-full border-t text-[10px] font-bold",
                          isActive
                            ? "border-white/10 text-white/60"
                            : "border-purple-200 text-gray-400",
                        )}
                      >
                        <div className="flex items-center gap-1">
                          <Layout className="h-3 w-3" />
                          {template.chapters} {t("Caps.")}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {template.estimatedTime}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
              {/* Público-Alvo */}
              <div className="space-y-2">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                  {t("Público Preferencial")}
                </label>
                <Select
                  value={ebookForm.targetAudience}
                  onChange={(v) =>
                    setEbookForm({ ...ebookForm, targetAudience: v })
                  }
                  options={[
                    {
                      value: "profissionais",
                      label: t("Profissionais de Área"),
                    },
                    {
                      value: "empresarios",
                      label: t("Empresários e Founders"),
                    },
                    {
                      value: "estudantes",
                      label: t("Estudantes / Graduandos"),
                    },
                    { value: "gerentes", label: t("Gerentes e Líderes") },
                    { value: "diretores", label: t("Diretores e C-Level") },
                  ]}
                  size="sm"
                  className="border-purple-200!"
                />
              </div>

              {/* Autor */}
              <div className="space-y-2">
                <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                  {t("Autor / Especialista (opcional)")}
                </label>
                <Input
                  type="text"
                  value={ebookForm.author}
                  onChange={(e) =>
                    setEbookForm({ ...ebookForm, author: e.target.value })
                  }
                  placeholder={t("Nome do autor para o rodapé")}
                  inputSize="sm"
                  className="bg-gray-50! focus:bg-white! shadow-inner"
                />
              </div>
            </div>

            {/* Empresa */}
            <div className="space-y-2">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
                {t("Identidade Corporativa (opcional)")}
              </label>
              <Input
                type="text"
                value={ebookForm.companyName}
                onChange={(e) =>
                  setEbookForm({ ...ebookForm, companyName: e.target.value })
                }
                placeholder={t("Sua Empresa ou Marca")}
                inputSize="sm"
                className="bg-gray-50! focus:bg-white! shadow-inner"
              />
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de Exclusão */}
      {deleteModalOpen && ebookToDelete && (
        <Modal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setEbookToDelete(null);
          }}
          title={t("Confirmar Exclusão")}
          subtitle={t("Esta ação removerá permanentemente o material")}
          icon={<AlertTriangle className="h-5 w-5" />}
          headerVariant="gradient"
          maxWidth="max-w-md"
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setEbookToDelete(null);
                }}
                variant="secondary"
                size="sm"
                className="border-purple-200"
              >
                {t("Manter Ebook")}
              </Button>
              <Button
                onClick={() => ebookToDelete && handleDelete(ebookToDelete)}
                variant="primary"
                size="sm"
                className="bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/20"
              >
                {t("Sim, Excluir")}
              </Button>
            </div>
          }
        >
          <div className="text-center space-y-4 p-2">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-3xl flex items-center justify-center mx-auto border border-red-100 mb-2">
              <AlertTriangle className="h-8 w-8" />
            </div>
            <p className="text-gray-700 text-base">
              {t("Tem certeza que deseja excluir o ebook")} <br />
              <strong className="text-gray-900 text-lg">
                "{ebookToDelete.title}"
              </strong>
              ?
            </p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-red-500 bg-red-50 inline-block px-4 py-1.5 rounded-full border border-red-100">
              {t("Atenção: Esta ação é irreversível")}
            </p>
          </div>
        </Modal>
      )}

      {/* Modal de Leads */}
      {showLeadsModal && selectedEbookForLeads && (
        <Modal
          open={showLeadsModal}
          onClose={() => {
            setShowLeadsModal(false);
            setSelectedEbookForLeads(null);
          }}
          title={t("Painel de Leads")}
          subtitle={`${t("Monitorando conversões:")} ${selectedEbookForLeads.title}`}
          icon={<Users className="h-5 w-5" />}
          headerVariant="gradient"
          maxWidth="max-w-6xl"
          footer={
            <div className="flex justify-end w-full">
              <Button
                onClick={() => {
                  setShowLeadsModal(false);
                  setSelectedEbookForLeads(null);
                }}
                variant="secondary"
                size="sm"
                className="px-8 border-purple-200"
              >
                {t("Fechar Monitor")}
              </Button>
            </div>
          }
        >
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-50/80 p-5 rounded-4xl border border-purple-200 shadow-inner">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-purple-600 shadow-xs border border-purple-200">
                  <Users className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-tight">
                    {t("Audiência Capturada")}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge
                      variant="purple"
                      size="sm"
                      className="font-bold text-[10px] uppercase"
                    >
                      {
                        leads.filter(
                          (lead) =>
                            lead.ebook_id === selectedEbookForLeads.ebook_id,
                        ).length
                      }{" "}
                      {t("Contatos")}
                    </Badge>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                      {t("Até o momento")}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={loadLeads}
                className="bg-white border-purple-200 hover:bg-purple-50 hover:border-purple-200 transition-all font-bold text-[10px] uppercase tracking-widest"
              >
                {t("Atualizar Dados")}
              </Button>
            </div>

            {(() => {
              const ebookLeads = leads.filter(
                (lead) => lead.ebook_id === selectedEbookForLeads.ebook_id,
              );
              return ebookLeads.length === 0 ? (
                <div className="text-center py-20 bg-gray-50/30 rounded-[2.5rem] border-2 border-dashed border-purple-200">
                  <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center mx-auto text-gray-300 shadow-xs mb-4">
                    <Users className="h-8 w-8" />
                  </div>
                  <p className="text-gray-500 font-bold uppercase tracking-widest text-xs">
                    {t("Aguardando as primeiras conversões...")}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-2">
                    {t(
                      "Os leads aparecerão aqui assim que baixarem o material.",
                    )}
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden bg-white border border-purple-200 rounded-4xl shadow-xs">
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="min-w-full divide-y divide-purple-100">
                      <thead className="bg-gray-50/80">
                        <tr>
                          <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                            {t("Contato Identificado")}
                          </th>
                          <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                            {t("Organização / Setor")}
                          </th>
                          <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                            {t("Qualidade")}
                          </th>
                          <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                            {t("Status")}
                          </th>
                          <th className="px-6 py-4 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">
                            {t("Conversão")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-purple-100">
                        {ebookLeads.map((lead) => (
                          <tr
                            key={lead.lead_id}
                            className="hover:bg-purple-50/50 transition-colors"
                          >
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-linear-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center text-purple-700 font-bold text-xs shadow-xs">
                                  {lead.name.substring(0, 1).toUpperCase()}
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-gray-900 leading-tight">
                                    {lead.name}
                                  </div>
                                  <div className="text-[11px] text-gray-500 font-medium">
                                    {lead.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="text-xs font-bold text-gray-700 uppercase tracking-tight">
                                {lead.company}
                              </div>
                              {lead.industry ? (
                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                  {lead.industry}
                                </div>
                              ) : (
                                <div className="text-[10px] text-gray-300 italic font-medium mt-0.5">
                                  {t("Setor não informado")}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></div>
                                <span
                                  className={cn(
                                    "text-[11px] font-bold uppercase tabular-nums",
                                    getScoreColor(lead.score?.total_score || 0),
                                  )}
                                >
                                  {lead.score?.total_score || 0}%{" "}
                                  {t("de Match")}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <span
                                className={cn(
                                  "px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-xs border",
                                  getLeadStatusColor(lead.status),
                                )}
                              >
                                {lead.status}
                              </span>
                            </td>
                            <td className="px-6 py-5 whitespace-nowrap">
                              <div className="flex items-center gap-2 text-gray-500">
                                <Calendar className="h-3.5 w-3.5 opacity-50" />
                                <span className="text-[11px] font-bold tabular-nums">
                                  {new Date(lead.created_at).toLocaleDateString(
                                    "pt-BR",
                                  )}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </Modal>
      )}

      {/* Editor de Ebook */}
      {selectedEbook && (
        <EbookEditor
          ebook={selectedEbook}
          onSave={handleSaveEbook}
          onClose={() => {
            setShowEditor(false);
            setSelectedEbook(null);
          }}
          isOpen={showEditor}
        />
      )}
    </PageLayout>
  );
};

export default EbookList;
